import { useCallback, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import * as zod from 'zod';
import { LggPopover } from 'src/components/general/display/popover';
import { Tooltip } from 'src/components/general/display/tooltip';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { Icon } from 'src/components/general/icon';
import { preventDefaultAndStopPropagation } from 'src/components/general/inputs/helpers';
import { TextInput } from 'src/components/general/inputs/text-input';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  TemplatePlaceholderOverlayContainer,
  TemplatePlaceholderOverlayFooter,
} from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible, VisibilityHandler } from 'src/hooks/use-visible';

const HeaderText = styled.p`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PopoverHeaderContainer = styled(FlexRow)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  justify-content: space-between;
  padding: 13px 15px;
`;

const PopoverHeaderLeftRailContainer = styled(FlexColumn)`
  width: 77%;
`;

const PopoverHeaderIcon = styled(Icon)`
  svg {
    cursor: pointer;
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const PopoverHeaderRemoveIcon = styled(PopoverHeaderIcon)`
  margin-right: 12px;

  svg {
    width: 18px;
    height: 18px;

    path {
      fill: ${({ theme }) => theme.colors.secondaryCoral};
    }
  }

  ${up('md')} {
    margin-right: 21px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const PopoverBody = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  padding: 15px 15px 55px;

  ${up('md')} {
    padding: 10px;
  }
`;

const PopoverHeaderTitle = styled(HeaderText)`
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  letter-spacing: 0;
  line-height: 18px;
`;

const PopoverHeaderSubTitle = styled(HeaderText)`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  letter-spacing: 0;
  line-height: 16px;
`;

const StyledTextInput = styled(TextInput)`
  height: 34px;
`;

type EditPlaceholderContentOverlayHeaderProps = {
  title: string;
  subTitle?: string;
  onRemove: VoidFunction;
  onClose: VoidFunction;
};

const EditPlaceholderContentPopoverHeader = ({
  title,
  subTitle,
  onRemove,
  onClose,
}: EditPlaceholderContentOverlayHeaderProps) => {
  return (
    <PopoverHeaderContainer>
      <PopoverHeaderLeftRailContainer>
        <PopoverHeaderTitle>{title}</PopoverHeaderTitle>
        {subTitle ? <PopoverHeaderSubTitle>{subTitle}</PopoverHeaderSubTitle> : null}
      </PopoverHeaderLeftRailContainer>
      <FlexRow>
        <PopoverHeaderRemoveIcon
          type="deleteBin"
          onClick={onRemove}
          data-lgg-id="remove-placeholder-node-overlay-option"
        />
        <PopoverHeaderIcon type="close" onClick={onClose} />
      </FlexRow>
    </PopoverHeaderContainer>
  );
};

const InputContainer = styled.div`
  padding: 5px;
`;

type EditPlaceholderContentOverlayProps = {
  value?: string;
  description?: string;
  onSave: (value: string) => void;
  onRemove: VoidFunction;
  visibilityHandler: VisibilityHandler;
};

export const EditPlaceholderContentOverlay = ({
  description,
  value,
  onSave,
  onRemove,
  visibilityHandler,
}: EditPlaceholderContentOverlayProps) => {
  const { t } = useTranslation(['common', 'conversations']);
  const placeholderFormSchema = useMemo(() => {
    return zod
      .object({
        value: zod.string(),
      })
      .required();
  }, []);
  const breakpointUpMd = useBreakpoint(up('md'));

  type FormValues = zod.infer<typeof placeholderFormSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(placeholderFormSchema),
    defaultValues: {
      value: '',
    },
    values: {
      value: value ?? '',
    },
    shouldFocusError: true,
  });

  useEffect(() => {
    if (!visibilityHandler.visible) {
      form.reset({ value: value ?? '' });
    }
  }, [form, value, visibilityHandler.visible]);

  const onSaveHandler = useCallback(async () => {
    await form.handleSubmit((data) => {
      onSave(data.value);
      visibilityHandler.close();
    })();
  }, [form, onSave, visibilityHandler]);

  const formContent = useMemo(
    () => (
      <FormProvider {...form}>
        <form
          onSubmit={async (e) => {
            preventDefaultAndStopPropagation(e);

            await onSaveHandler();
          }}
        >
          <PopoverBody>
            <InputContainer>
              <Controller
                control={form.control}
                name="value"
                render={({ field, fieldState }) => (
                  <StyledTextInput
                    {...field}
                    value={field.value}
                    label=""
                    placeholder={t(
                      'conversations:messageInput.options.messageTemplates.editPlaceholderOverlay.inputPlaceholder',
                    )}
                    autoComplete="off"
                    error={Boolean(fieldState.error)}
                    reserveErrorArea={false}
                    data-lgg-id="edit-placeholder-content-input"
                  />
                )}
              />
            </InputContainer>
          </PopoverBody>
          <TemplatePlaceholderOverlayFooter
            onClose={visibilityHandler.close}
            submitTextOverride={t('common:save')}
          />
        </form>
      </FormProvider>
    ),
    [form, onSaveHandler, t, visibilityHandler.close],
  );

  return breakpointUpMd ? (
    <LggPopover
      content={
        <TemplatePlaceholderOverlayContainer data-lgg-id="edit-placeholder-content-overlay">
          <EditPlaceholderContentPopoverHeader
            title={t(
              'conversations:messageInput.options.messageTemplates.editPlaceholderOverlay.title',
            )}
            onRemove={onRemove}
            subTitle={description}
            onClose={visibilityHandler.close}
          />
          {formContent}
        </TemplatePlaceholderOverlayContainer>
      }
      hideArrow
      placement="top"
      trigger={['click']}
      visible={visibilityHandler.visible}
      afterVisibleChange={visibilityHandler.setVisible}
      onVisibleChange={visibilityHandler.setVisible}
    >
      <span></span>
    </LggPopover>
  ) : (
    <BottomDrawer
      visible={visibilityHandler.visible}
      subTitle={description}
      title={t(
        'conversations:messageInput.options.messageTemplates.editPlaceholderOverlay.titleShort',
      )}
      trailing={<PopoverHeaderRemoveIcon type="deleteBin" onClick={onRemove} />}
      onClose={visibilityHandler.close}
    >
      {formContent}
    </BottomDrawer>
  );
};

type TemplatePlaceholderProps = {
  children: React.ReactChild;
  value?: string;
  description?: string;
  onSave: (value?: string) => void;
  onRemove: VoidFunction;
  onPopoverVisibilityChange?: (visible: boolean) => void;
  isActive: boolean;
};

export const TemplatePlaceholder = ({
  children,
  onRemove,
  onSave,
  value,
  description,
  onPopoverVisibilityChange,
  isActive,
}: TemplatePlaceholderProps) => {
  const visibilityHandler = useVisible(isActive, onPopoverVisibilityChange);

  return (
    <span>
      <Tooltip title={description}>
        <span onClick={visibilityHandler.show}>{children}</span>
      </Tooltip>
      <EditPlaceholderContentOverlay
        description={description}
        onRemove={onRemove}
        onSave={onSave}
        visibilityHandler={visibilityHandler}
        value={value}
      />
    </span>
  );
};
